/**
 * ShopSales
 * --
 */
import { useState, useEffect } from 'react';
import { Content, Title, Row, Col } from 'components';
import { Divider, Button, Table, Radio, Spin, Statistic } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import {
  CartesianGrid,
  Tooltip,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Bar,
} from 'recharts';
import MessageAlert, { strCut, stringToMoneyFormat } from 'utils';
import moment from 'moment';
import dayjs from 'dayjs';
import { StatisticsApi, OrderApi } from 'api';

const DAY = -10;
const MOMENT_FORMAT = 'YYYY-MM-DD';
const endDate = moment().format(MOMENT_FORMAT);
const startDate = moment().add(DAY, 'days').format(MOMENT_FORMAT);
const today = moment().format('YYYY-MM-DD(HH:mm:ss)');

/**----------------------------------------
 * [Component] 홈 > 매장별 매출 차트
 * ----------------------------------------
 */
const ShopSales = ({ data, isMobile }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */

  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        <Row gutter={[5, 5]}>
          {/* ===== 차트: start ===== */}
          <Col x={isMobile ? 24 : 10}>
            <Content
              maxWidth={'100%'}
              borderRadius={4}
              padding={isMobile ? 10 : 20}
              // height={450}
              height={isMobile ? 325 : 465}
              style={{ overflowY: 'auto' }}
            >
              <p size={5}>{today}기준</p>

              <ResponsiveContainer
                width="100%"
                minWidth={400}
                // height={385}
                height={isMobile ? 260 : 390}
                style={{ marginTop: 15 }}
              >
                <BarChart
                  width={500}
                  // height={385}
                  height={isMobile ? 260 : 390}
                  data={
                    !data
                      ? []
                      : data?.map((item) => ({
                          ...item,
                          date: moment(item.date).format('MM/DD'),
                          ['amt']: Number(item.totalAmount),
                          ['총매출']: Number(item.totalAmount ?? 0),
                          ['순매출']: Number(item.netsAmount ?? 0),
                        }))
                  }
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="6 7" />
                  {!isMobile && <YAxis dataKey="총매출" />}
                  <XAxis type="category" dataKey="date" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="총매출" fill="#82ca9d" />
                  <Bar dataKey="순매출" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Content>
          </Col>
          {/* ===== 차트: end ===== */}
          {/* ===== 테이블: start ===== */}
          <Col x={isMobile ? 24 : 14}>
            <Content
              maxWidth={'100%'}
              height={450}
              borderRadius={4}
              // padding={'14px 5px'}
              padding={isMobile ? 10 : 20}
            >
              <Title
                size={5}
                style={{ marginBottom: 12 }}
                subButton={
                  <Button
                    size="small"
                    onClick={() => window.open('/order/dashboard', '_blank')}
                  >
                    <ExportOutlined /> 매출관리
                  </Button>
                }
              >
                쇼핑몰 일별 매출
              </Title>
              <Divider style={{ margin: '12px 0' }} />

              <Table
                bordered
                size="small"
                scroll={{ x: 720, y: 290 }}
                pagination={false}
                dataSource={data ?? []}
                style={{ borderBottom: '1px solid #f1f1f1' }}
                columns={[
                  {
                    title: '날짜',
                    dataIndex: 'date',
                    width: 100,
                    align: 'center',
                    ...(!isMobile && { fixed: 'left' }),
                    render: (v) => moment(v).format('YYYY-MM-DD(ddd)'),
                  },
                  {
                    title: '총매출',
                    dataIndex: 'totalAmount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}원`,
                  },
                  {
                    title: '순매출',
                    dataIndex: 'netsAmount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}원`,
                  },
                  {
                    title: '주문건수',
                    dataIndex: 'paymentCount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}건`,
                  },
                  {
                    title: '할인금액',
                    dataIndex: 'discountAmount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}원`,
                  },
                ]}
                summary={(row) => {
                  const totalAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.totalAmount),
                    0
                  );
                  const netsAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.netsAmount),
                    0
                  );
                  const paymentCount = row.reduce(
                    (acc, cur) => acc + Number(cur.paymentCount),
                    0
                  );
                  const discountAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.discountAmount),
                    0
                  );

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row align="center">
                        <Table.Summary.Cell index={0}>
                          <b>합계</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <b>{stringToMoneyFormat(totalAmount)}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                          <b>{stringToMoneyFormat(netsAmount)}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <b>{stringToMoneyFormat(paymentCount)}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <b>{stringToMoneyFormat(discountAmount)}</b>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Content>
          </Col>
          {/* ===== 테이블: end ===== */}
        </Row>
      </Spin>
    </>
  ); // JSX: end
};

ShopSales.defaultProps = {
  onSelect: () => {},
};
export default ShopSales;
